import ReactGA from 'react-ga4';

const isProduction = process.env.NODE_ENV === 'production';
const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;

class AnalyticsService {
  constructor() {
    this.initialized = false;
  }

  init() {
    if (!measurementId || measurementId === 'G-YOUR_STAGING_ID') {
      console.log('Google Analytics not configured - skipping initialization');
      return;
    }

    try {
      ReactGA.initialize(measurementId, {
        gaOptions: {
          anonymizeIp: true,
          cookieFlags: 'SameSite=None;Secure'
        },
        gtagOptions: {
          debug_mode: !isProduction,
          send_page_view: false
        }
      });
      
      this.initialized = true;
      console.log(`Google Analytics initialized with ID: ${measurementId}`);
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  pageView(path, title) {
    if (!this.initialized) return;
    
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title
    });
  }

  event(eventName, parameters = {}) {
    if (!this.initialized) return;

    ReactGA.event(eventName, parameters);
  }

  // Legacy method for backwards compatibility
  eventLegacy(category, action, label = null, value = null, additionalParams = {}) {
    if (!this.initialized) return;

    const eventName = `${category.toLowerCase()}_${action.toLowerCase()}`;
    const parameters = {
      category,
      action,
      ...(label && { label }),
      ...(value !== null && { value }),
      ...additionalParams
    };

    this.event(eventName, parameters);
  }

  // Risk Assessment Events
  trackRiskAssessmentStart(assessmentType) {
    this.event('risk_assessment_start', {
      assessment_type: assessmentType,
      category: 'risk_assessment'
    });
  }

  trackRiskAssessmentComplete(assessmentType, duration) {
    this.event('risk_assessment_complete', {
      assessment_type: assessmentType,
      duration_seconds: Math.round(duration),
      category: 'risk_assessment'
    });
  }

  trackRiskAssessmentError(assessmentType, errorType) {
    this.event('risk_assessment_error', {
      assessment_type: assessmentType,
      error_type: errorType,
      category: 'risk_assessment'
    });
  }

  trackRiskAssessmentRetry(assessmentType) {
    this.event('risk_assessment_retry', {
      assessment_type: assessmentType,
      category: 'risk_assessment'
    });
  }

  // Subscription Events
  trackSubscriptionPlanView(planName, price) {
    this.event('view_item', {
      item_category: 'subscription_plan',
      item_name: planName,
      value: price,
      currency: 'EUR'
    });
  }

  trackSubscriptionPlanSelect(planName, billingPeriod, price) {
    this.event('select_item', {
      item_category: 'subscription_plan',
      item_name: planName,
      billing_period: billingPeriod,
      value: price,
      currency: 'EUR'
    });
  }

  trackSubscriptionCheckoutStart(planName, billingPeriod) {
    this.event('begin_checkout', {
      item_category: 'subscription_plan',
      item_name: planName,
      billing_period: billingPeriod
    });
  }

  trackSubscriptionCheckoutComplete(planName, billingPeriod, price) {
    this.event('purchase', {
      transaction_id: `${Date.now()}_${planName}`,
      value: price,
      currency: 'EUR',
      items: [{
        item_id: `${planName}_${billingPeriod}`,
        item_name: planName,
        item_category: 'subscription',
        price: price,
        quantity: 1
      }]
    });
  }

  trackSubscriptionCancel(planName) {
    this.event('subscription_cancel', {
      plan_name: planName,
      category: 'subscription'
    });
  }

  trackFreeTrialStart() {
    this.event('trial_start', {
      category: 'subscription'
    });
  }

  // User Events
  trackLogin(method = 'email') {
    this.event('login', {
      method: method
    });
  }

  trackSignup(method = 'email') {
    this.event('sign_up', {
      method: method
    });
  }

  trackLogout() {
    this.event('logout');
  }

  // Feature Usage Events
  trackFeatureUse(featureName, details = null) {
    this.event('feature_use', {
      feature_name: featureName,
      category: 'engagement',
      ...(details && { details })
    });
  }

  trackButtonClick(buttonName, section) {
    this.event('button_click', {
      button_name: buttonName,
      section: section,
      category: 'ui_interaction'
    });
  }

  trackFormSubmit(formName, success = true) {
    this.event('form_submit', {
      form_name: formName,
      success: success,
      category: 'engagement'
    });
  }

  trackFileUpload(fileType, fileSize) {
    this.event('file_upload', {
      file_type: fileType,
      file_size_kb: Math.round(fileSize / 1024),
      category: 'engagement'
    });
  }

  trackDownload(documentType) {
    this.event('file_download', {
      file_type: documentType,
      category: 'engagement'
    });
  }

  // Error Tracking
  trackError(errorMessage, errorLocation) {
    this.event('exception', {
      description: `${errorLocation}: ${errorMessage}`,
      fatal: false
    });
  }

  // Set User Properties
  setUserProperties(properties) {
    if (!this.initialized) return;
    
    ReactGA.gtag('set', 'user_properties', properties);
  }

  setUserId(userId) {
    if (!this.initialized) return;
    
    ReactGA.gtag('config', measurementId, {
      user_id: userId
    });
  }

  // E-commerce tracking for subscriptions
  trackPurchase(transactionId, value, currency = 'USD', items = []) {
    if (!this.initialized) return;
    
    ReactGA.event('purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items
    });
  }
}

const analytics = new AnalyticsService();
export default analytics;